import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";
import type { KidProfile, SessionSummary, KidTabProps } from "./types";

type StatsData = Pick<KidProfile, "sessions" | "avgBattingScore" | "avgBowlingScore" | "avgFieldingScore">;

interface Props extends KidTabProps<StatsData> {}

// Average the non-null values in an array
function avg(vals: (number | null)[]): number | null {
  const nums = vals.filter((v): v is number => v != null);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

interface SparkPoint {
  label: string;  // abbreviated date
  value: number | null;
}

type Discipline = "batting" | "bowling" | "fielding";

function sessionDisciplineAvg(s: SessionSummary, discipline: Discipline): number | null {
  if (!s.attended || !s.skills) return null;
  const sk = s.skills;
  if (discipline === "batting")
    return avg([sk.battingGrip, sk.battingStance, sk.battingFootwork, sk.battingTransfer, sk.battingHead]);
  if (discipline === "bowling")
    return avg([sk.bowlingLoad, sk.bowlingBfc, sk.bowlingBrace, sk.bowlingRelease]);
  return avg([sk.fieldingCatch, sk.fieldingThrow, sk.fieldingGround]);
}

function trendLabel(points: SparkPoint[]): "Improving" | "Steady" | "Needs work" {
  const vals = points.map((p) => p.value).filter((v): v is number => v != null);
  if (vals.length < 4) return "Steady";
  const half = Math.floor(vals.length / 2);
  const first = avg(vals.slice(0, half)) ?? 0;
  const last = avg(vals.slice(-half)) ?? 0;
  const delta = last - first;
  if (delta >= 0.3) return "Improving";
  if (delta <= -0.3) return "Needs work";
  return "Steady";
}

function trendBadgeClass(trend: ReturnType<typeof trendLabel>) {
  if (trend === "Improving") return "bg-green-100 text-green-800 border-green-200 border";
  if (trend === "Needs work") return "bg-red-100 text-red-800 border-red-200 border";
  return "bg-gray-100 text-gray-600 border-gray-200 border";
}

function NarrativeSlot({ narrative }: { narrative: Props["narrative"] }) {
  if (!narrative) return null;
  return (
    <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm text-teal-800 mb-5">
      {narrative}
    </div>
  );
}

interface DisciplineCardProps {
  label: string;
  currentAvg: number | null;
  points: SparkPoint[];
  color: string;
}

function DisciplineCard({ label, currentAvg, points, color }: DisciplineCardProps) {
  const trend = trendLabel(points);
  const hasData = points.some((p) => p.value != null);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">
            {currentAvg != null ? currentAvg.toFixed(1) : "—"}
            <span className="text-xs font-normal text-gray-400 ml-1">/ 5</span>
          </p>
        </div>
        <Badge className={`text-xs ${trendBadgeClass(trend)} hover:${trendBadgeClass(trend)}`}>
          {trend}
        </Badge>
      </div>

      {hasData ? (
        <ResponsiveContainer width="100%" height={90}>
          <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis domain={[1, 5]} tick={{ fontSize: 9, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ fontSize: 11, padding: "4px 8px" }}
              formatter={(v: number) => [v != null ? v.toFixed(2) : "—", label]}
              labelFormatter={(l) => l}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3, fill: color }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-xs text-gray-400 py-6 text-center">No data captured yet</p>
      )}
    </div>
  );
}

export function KidStatsTab({ data, isLoading, isEmpty, error, onRetry, narrative }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-6 w-48" />
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-gray-500">{error}</p>
        {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>}
      </div>
    );
  }

  if (isEmpty || data.sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
        <p className="font-medium text-gray-500">No session data yet</p>
        <p className="text-sm mt-1">Stats will appear after the first session is logged.</p>
      </div>
    );
  }

  // Sessions are newest-first — reverse for chronological sparkline left→right
  const chronological = [...data.sessions].reverse();

  function buildPoints(discipline: Discipline): SparkPoint[] {
    return chronological.map((s) => ({
      label: s.date.slice(5),  // "MM-DD"
      value: sessionDisciplineAvg(s, discipline),
    }));
  }

  const disciplines: { label: string; discipline: Discipline; avg: number | null; color: string }[] = [
    { label: "Batting", discipline: "batting", avg: data.avgBattingScore, color: "#3b82f6" },
    { label: "Bowling", discipline: "bowling", avg: data.avgBowlingScore, color: "#6366f1" },
    { label: "Fielding", discipline: "fielding", avg: data.avgFieldingScore, color: "#0ea5e9" },
  ];

  return (
    <div className="space-y-4 py-2" data-testid="tab-content-stats-loaded">
      <NarrativeSlot narrative={narrative} />
      {disciplines.map(({ label, discipline, avg: currentAvg, color }) => (
        <DisciplineCard
          key={discipline}
          label={label}
          currentAvg={currentAvg}
          points={buildPoints(discipline)}
          color={color}
        />
      ))}
    </div>
  );
}
